import { css } from 'aphrodite';
import * as React from 'react';
import { TicketPriority } from '../../../../models/Support/SupportEnums';
import { Field, FieldDescription, FieldError, FieldLabel, FieldTitle } from '../../../../web/components-v2/Field';
import { Input } from '../../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import { supportSharedStyles as shared } from '../styles/supportSharedStyles';
import { AccountField } from './AccountField';
import { ContactField } from './ContactField';
import { newTicketStyles as s } from './styles';
import type { NewTicketFormState } from './useNewTicketFormState';

const PRIORITY_OPTIONS = [TicketPriority.Low, TicketPriority.Normal, TicketPriority.High, TicketPriority.Urgent];
const NO_CATEGORY = 'none';

interface NewTicketFieldsProps {
	/** Rendered between Subject and the priority row — the description editor for an internal ticket. */
	children?: React.ReactNode;
	contactLabel: string;
	/** Shown under the contact picker when the mode needs it, e.g. to say the address is the recipient. */
	contactHint?: React.ReactNode;
	form: NewTicketFormState;
	subjectPlaceholder: string;
}

export const NewTicketFields: React.FC<NewTicketFieldsProps> = ({
	children,
	contactHint,
	contactLabel,
	form,
	subjectPlaceholder,
}) => (
	<>
		{form.mailboxesFailed ? (
			<div className={css(shared.modalErrorText)} role='status'>
				We couldn't load your mailboxes, so ticket creation is disabled. Refresh and try again.
			</div>
		) : (
			form.hasNoAgentMailbox && (
				<div className={css(shared.modalErrorText)} role='status'>
					You're not an agent in any support mailbox, so ticket creation is disabled. Ask a mailbox admin to add you to
					the team.
				</div>
			)
		)}
		{form.mailboxes.length > 1 && (
			<Field>
				<FieldLabel htmlFor='new-ticket-mailbox'>Mailbox</FieldLabel>
				<Select
					items={form.mailboxes.map(mailbox => ({ value: mailbox.id, label: mailbox.name }))}
					value={form.mailboxId}
					onValueChange={form.setSelectedMailboxId}
				>
					<SelectTrigger id='new-ticket-mailbox' styles={s.selectTrigger}>
						<SelectValue placeholder='Select a mailbox' />
					</SelectTrigger>
					<SelectContent>
						{form.mailboxes.map(mailbox => (
							<SelectItem key={mailbox.id} value={mailbox.id}>
								{mailbox.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</Field>
		)}
		<Field>
			<FieldTitle>Account</FieldTitle>
			<AccountField account={form.account} onChange={form.changeAccount} />
			{form.account && <FieldDescription>CSM: {form.csmName || 'Unassigned'}</FieldDescription>}
		</Field>
		<Field>
			<FieldTitle>{contactLabel}</FieldTitle>
			<ContactField
				account={form.account}
				contact={form.contact}
				manualContact={form.manualContact}
				manualEmailError={form.contactError}
				onChangeContact={form.changeContact}
				onToggleManual={form.toggleManualContact}
			/>
			{form.contactError
				? !form.manualContact && <FieldError styles={[s.contactError]}>{form.contactError}</FieldError>
				: contactHint && <FieldDescription>{contactHint}</FieldDescription>}
		</Field>
		<Field>
			<FieldLabel htmlFor='new-ticket-subject'>Subject</FieldLabel>
			<Input
				id='new-ticket-subject'
				styles={[shared.modalInput]}
				type='text'
				placeholder={subjectPlaceholder}
				value={form.subject}
				onChange={e => form.setSubject(e.target.value)}
			/>
		</Field>
		{children}
		<div className={css(s.fieldRow)}>
			<Field>
				<FieldLabel htmlFor='new-ticket-priority'>Priority</FieldLabel>
				<Select value={form.priority} onValueChange={value => form.setPriority(value as TicketPriority)}>
					<SelectTrigger id='new-ticket-priority' styles={s.selectTrigger}>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{PRIORITY_OPTIONS.map(option => (
							<SelectItem key={option} value={option}>
								{option}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</Field>
			{form.categories.length > 0 && (
				<Field>
					<FieldLabel htmlFor='new-ticket-category'>Category</FieldLabel>
					<Select
						items={[
							{ value: NO_CATEGORY, label: 'None' },
							...form.categories.map(category => ({ value: category.id, label: category.name })),
						]}
						value={form.validCategoryId || NO_CATEGORY}
						onValueChange={value => form.setCategoryId(value === NO_CATEGORY ? '' : value)}
					>
						<SelectTrigger id='new-ticket-category' styles={s.selectTrigger}>
							<SelectValue placeholder='None' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={NO_CATEGORY}>None</SelectItem>
							{form.categories.map(category => (
								<SelectItem key={category.id} value={category.id}>
									{category.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</Field>
			)}
		</div>
	</>
);
